import isFunction from 'lodash/isFunction';
import { Component } from 'react';
import { FormContext } from './formContext';
import {
    isEmailValid, isPasswordValid, isPhoneNumberValid, isDateValid
} from './Validators';

const hasPropChanged = (propName, props, prevProps) => props[propName] !== prevProps[propName];

class FormControl extends Component {
    constructor(props) {
        super(props);

        const hasValidators = props.validators && props.validators.length;
        const hasCustomValidator = isFunction(props.customValidator);

        this.state = {
            isDirty: false,
            isValid: !(hasValidators || hasCustomValidator),
            error: props.error || null,
            // eslint-disable-next-line react/no-unused-state
            type: 'error'
        };

        this.translations = {
            required: 'form.required_field_error',
            email: 'email_validation_label',
            password: 'customer.password_complexity.error',
            phone: 'customer.phone_validation.error',
            date: 'form.date_validation_error',
            ...(props.translationOverrides || {})
        };

        if (props.initialError) {
            this.state.isValid = false;
            this.state.error = props.initialError;
        }
    }

    componentDidMount() {
        this.context.form?.attachToForm(this);

        if (this.context.form?.validateOnInit) {
            const { value } = this.props;
            this.validate({ isDirty: !!value });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        return this.state !== nextState || this.props !== nextProps;
    }

    componentDidUpdate(prevProps) {
        const { props } = this;

        if (
            hasPropChanged('value', props, prevProps)
            || hasPropChanged('error', props, prevProps)
            || hasPropChanged('customValidator', props, prevProps)
        ) {
            this.validate();
        }
    }

    componentWillUnmount() {
        this.context.form?.detachFromForm(this);
    }

    handleFormValidity(hasValidityChanged) {
        if (hasValidityChanged) {
            this.context.form?.handleValidity();
        }
    }

    processInputValueValidation() {
        const {
            translate,
            customValidator,
            error,
            validators,
            value,
            min,
            max
        } = this.props;

        let response = { isValid: true, error: null, type: null };

        // Show error from props
        if (error) {
            return {
                isValid: false,
                type: 'error',
                error
            };
        }

        if (validators) {
            let errorTranslationKey;

            if (validators.indexOf('required') !== -1 && !value) {
                errorTranslationKey = this.translations.required;
            }

            if (validators.indexOf('email') !== -1 && value && !isEmailValid(value)) {
                errorTranslationKey = this.translations.email;
            }

            if (validators.indexOf('password') !== -1 && value && !isPasswordValid(value)) {
                errorTranslationKey = this.translations.password;
            }

            if (validators.indexOf('phone') !== -1 && value && !isPhoneNumberValid(value)) {
                errorTranslationKey = this.translations.phone;
            }

            if (validators.indexOf('date') !== -1 && value && !isDateValid(value, min, max)) {
                errorTranslationKey = this.translations.date;
            }

            if (errorTranslationKey) {
                return {
                    isValid: false,
                    error: translate(errorTranslationKey),
                    type: 'error'
                };
            }
        }

        if (customValidator) {
            response = customValidator(value);
        }

        return response;
    }

    // eslint-disable-next-line react/no-unused-class-component-methods
    isErrorVisible() {
        const { isValid, isDirty } = this.state;
        const { initialError } = this.props;

        return !isValid && (this.context.form?.isSubmitted || isDirty || initialError);
    }

    // eslint-disable-next-line react/no-unused-class-component-methods
    reset() {
        this.setState({ isValid: false, isDirty: false });
    }

    validate(extraState = {}) {
        const { isDirty } = this.state;
        const { isValid, error, type } = this.processInputValueValidation();
        const hasValidityChanged = isValid !== this.state.isValid;

        const state = {
            isValid,
            isDirty,
            initialError: null,
            error,
            type,
            ...extraState
        };

        this.setState(state, () => this.handleFormValidity(hasValidityChanged));

        return state.isValid;
    }
}

FormControl.contextType = FormContext;

export default FormControl;
